import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Plus, Calendar, Clock, CheckCircle2, XCircle, Loader2,
  CalendarDays, User, FileText, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const LEAVE_TYPES = ['casual', 'sick', 'earned', 'unpaid'];

export default function CRMLeave() {
  const { token, user, isAdmin } = useCRMAuth();
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [rejectReason, setRejectReason] = useState('');
  
  const [form, setForm] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => { fetchData(); }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const userParam = !isAdmin() ? `${statusParam ? '&' : '?'}user_id=${user?.id}` : '';
      
      const [leavesRes, balanceRes] = await Promise.all([
        fetch(`${API}/api/crm/leave/${statusParam}${userParam}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/crm/leave/balance/${user?.id}`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (leavesRes.ok) setLeaves((await leavesRes.json()).leaves || []);
      if (balanceRes.ok) setBalance((await balanceRes.json()).balance);
    } catch (err) {}
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) { toast.error('Please select dates'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/leave/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          user_id: user?.id,
          user_name: user?.name,
          start_date: new Date(form.start_date).toISOString(),
          end_date: new Date(form.end_date).toISOString()
        })
      });
      if (res.ok) {
        toast.success('Leave request submitted!');
        setShowModal(false);
        setForm({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
        fetchData();
      } else {
        toast.error('Failed to submit');
      }
    } catch (err) { toast.error('Failed to submit'); }
    finally { setSaving(false); }
  };

  const handleApprove = async (leaveId) => {
    try {
      const res = await fetch(`${API}/api/crm/leave/${leaveId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: user?.id, approved_by_name: user?.name })
      });
      if (res.ok) { toast.success('Leave approved!'); fetchData(); }
    } catch (err) { toast.error('Failed to approve'); }
  };

  const handleReject = async () => {
    if (!selectedLeave) return;
    try {
      const res = await fetch(`${API}/api/crm/leave/${selectedLeave.id}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: user?.id, approved_by_name: user?.name, reason: rejectReason })
      });
      if (res.ok) { toast.success('Leave rejected'); setShowRejectModal(false); setRejectReason(''); fetchData(); }
    } catch (err) { toast.error('Failed to reject'); }
  };

  const handleCancel = async (leaveId) => {
    if (!confirm('Cancel this leave request?')) return;
    try {
      const res = await fetch(`${API}/api/crm/leave/${leaveId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Leave cancelled'); fetchData(); }
    } catch (err) { toast.error('Failed to cancel'); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <CRMLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leave Management</h1>
          <p className="text-slate-500">Request and manage leaves</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Request Leave
        </Button>
      </div>

      {/* Leave Balance Cards */}
      {balance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border-l-4 border-blue-500">
            <p className="text-sm text-slate-500">Casual Leave</p>
            <p className="text-2xl font-bold text-slate-900">{balance.casual?.remaining || 0}<span className="text-sm text-slate-400">/{balance.casual?.total || 12}</span></p>
          </Card>
          <Card className="p-4 border-l-4 border-red-500">
            <p className="text-sm text-slate-500">Sick Leave</p>
            <p className="text-2xl font-bold text-slate-900">{balance.sick?.remaining || 0}<span className="text-sm text-slate-400">/{balance.sick?.total || 6}</span></p>
          </Card>
          <Card className="p-4 border-l-4 border-emerald-500">
            <p className="text-sm text-slate-500">Earned Leave</p>
            <p className="text-2xl font-bold text-slate-900">{balance.earned?.remaining || 0}<span className="text-sm text-slate-400">/{balance.earned?.total || 15}</span></p>
          </Card>
          <Card className="p-4 border-l-4 border-slate-500">
            <p className="text-sm text-slate-500">Unpaid Leave</p>
            <p className="text-2xl font-bold text-slate-900">{balance.unpaid?.used || 0}<span className="text-sm text-slate-400"> taken</span></p>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'rejected'].map(s => (
            <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm" onClick={() => setFilter(s)} className={filter === s ? 'bg-indigo-600' : ''}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </Card>

      {/* Leave List */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : leaves.length === 0 ? (
        <Card className="p-12 text-center">
          <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No leave requests</h3>
          <p className="text-slate-500">Click "Request Leave" to submit a new request</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {leaves.map(leave => (
            <Card key={leave.id} className="p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">{leave.user_name}</h3>
                      {getStatusBadge(leave.status)}
                      <Badge variant="outline">{leave.leave_type}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />{formatDate(leave.start_date)} - {formatDate(leave.end_date)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{leave.days} day(s)</span>
                    </div>
                    {leave.reason && <p className="text-sm text-slate-600 mt-2">{leave.reason}</p>}
                    {leave.rejection_reason && <p className="text-sm text-red-600 mt-2"><AlertTriangle className="w-3 h-3 inline mr-1" />Rejection: {leave.rejection_reason}</p>}
                    {leave.approved_by_name && leave.status !== 'pending' && (
                      <p className="text-xs text-slate-400 mt-1">{leave.status === 'approved' ? 'Approved' : 'Rejected'} by {leave.approved_by_name}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {leave.status === 'pending' && isAdmin() && (
                    <>
                      <Button size="sm" onClick={() => handleApprove(leave.id)} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => { setSelectedLeave(leave); setShowRejectModal(true); }}>
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {leave.status === 'pending' && leave.user_id === user?.id && (
                    <Button size="sm" variant="outline" onClick={() => handleCancel(leave.id)}>Cancel</Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Request Leave Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Leave Type</label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({...form, leave_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)} Leave</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Start Date</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">End Date</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} min={form.start_date} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Reason</label>
              <Textarea placeholder="Reason for leave..." value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject Leave Request</DialogTitle></DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Reason for Rejection</label>
            <Textarea placeholder="Enter reason..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CRMLayout>
  );
}
